import { useRef, useState } from 'react';
import emailjs from '@emailjs/browser';
import Button from '../components/common/Button';
import { emailJsConfig } from '../config/site';

const contactMethods = [
  {
    label: 'Phone',
    value: '(437) 435-7083',
    href: 'tel:+14374357083',
  },
  {
    label: 'Email',
    value: 'contact@evonindustrial.com',
    href: 'mailto:contact@evonindustrial.com',
  },
  {
    label: 'Service Area',
    value: 'Canada-wide',
  },
];

const quoteChecklist = [
  'Equipment type, brand, model, or nameplate photo',
  'Symptoms such as noise, vibration, pressure loss, overheating, or drift',
  'Location, urgency, and any downtime constraints',
  'Whether pickup, shipping, or emergency repair support is needed',
  'Manufacturer, model, ratio, size, or other equipment details if available',
];

function Contact() {
  const formRef = useRef(null);
  const formStartedAtRef = useRef(Date.now());
  const [status, setStatus] = useState('idle');
  const [statusMessage, setStatusMessage] = useState('');

  const isEmailJsConfigured = Boolean(emailJsConfig.serviceId && emailJsConfig.templateId && emailJsConfig.publicKey);
  const isSubmitting = status === 'sending';

  const handleSubmit = (event) => {
    event.preventDefault();

    const formData = new FormData(event.currentTarget);
    const honeypotValue = formData.get('website');
    const secondsToSubmit = (Date.now() - formStartedAtRef.current) / 1000;

    if (honeypotValue || secondsToSubmit < 3) {
      setStatus('success');
      setStatusMessage('Thanks. Your repair request was sent successfully.');
      event.currentTarget.reset();
      formStartedAtRef.current = Date.now();
      return;
    }

    if (!isEmailJsConfigured) {
      setStatus('error');
      setStatusMessage('Email service is not configured yet. Please call or email Evon directly.');
      return;
    }

    setStatus('sending');
    setStatusMessage('Sending your repair request...');

    emailjs
      .sendForm(emailJsConfig.serviceId, emailJsConfig.templateId, formRef.current, {
        publicKey: emailJsConfig.publicKey,
      })
      .then(() => {
        setStatus('success');
        setStatusMessage('Thanks. Your repair request was sent successfully.');
        formRef.current.reset();
        formStartedAtRef.current = Date.now();
      })
      .catch((error) => {
        console.error('EmailJS send failed:', error);
        setStatus('error');
        setStatusMessage(
          error?.text
            ? `EmailJS error: ${error.text}. Please call or email us directly.`
            : 'Something went wrong while sending. Please call or email us directly.'
        );
      });
  };

  return (
    <>
      <section className="contact-page-hero" aria-labelledby="contact-page-title">
        <div className="container contact-page-hero__inner">
          <div>
            <p className="eyebrow">Contact</p>
            <h1 id="contact-page-title">Start a gearbox or hydraulic repair conversation</h1>
            <p>
              Send the details for your industrial gearbox, hydraulic cylinder, pump, drive component, or related
              mechanical repair request. We support repair inquiries across Canada with emergency repair support, free
              pickup and shipping, and 1-year warranty coverage.
            </p>
          </div>
          <div className="contact-page-methods" aria-label="Contact methods">
            {contactMethods.map((method) => (
              <article key={method.label} className="contact-page-method">
                <span>{method.label}</span>
                {method.href ? <a href={method.href}>{method.value}</a> : <p>{method.value}</p>}
              </article>
            ))}
          </div>
        </div>
      </section>

      <section className="contact-page-section">
        <div className="container contact-page-grid">
          <aside className="contact-page-sidebar" aria-labelledby="contact-sidebar-title">
            <p className="eyebrow">Before You Send</p>
            <h2 id="contact-sidebar-title">Helpful repair details</h2>
            <p>
              The more context you can provide, the easier it is to review the repair need and follow up with a practical
              next step.
            </p>
            <ul>
              {quoteChecklist.map((item) => (
                <li key={item}>{item}</li>
              ))}
            </ul>
          </aside>

          <form ref={formRef} className="contact-page-form" aria-label="Repair contact form" onSubmit={handleSubmit}>
            <label className="contact-page-form__trap" aria-hidden="true">
              Website
              <input type="text" name="website" tabIndex="-1" autoComplete="off" />
            </label>

            <div className="contact-page-form__row">
              <label>
                Name
                <input type="text" name="from_name" autoComplete="name" required />
              </label>
              <label>
                Company
                <input type="text" name="company" autoComplete="organization" />
              </label>
            </div>

            <div className="contact-page-form__row">
              <label>
                Email
                <input type="email" name="reply_to" autoComplete="email" required />
              </label>
              <label>
                Phone
                <input type="tel" name="phone" autoComplete="tel" />
              </label>
            </div>

            <div className="contact-page-form__row">
              <label>
                Service needed
                <select name="service_type" defaultValue="" required>
                  <option value="" disabled>
                    Select a service
                  </option>
                  <option value="Gearbox repair">Gearbox repair</option>
                  <option value="Hydraulic repair">Hydraulic repair</option>
                  <option value="Pump or cylinder repair">Pump or cylinder repair</option>
                  <option value="Industrial mechanical repair">Industrial mechanical repair</option>
                  <option value="Not sure">Not sure</option>
                </select>
              </label>
              <label>
                Urgency
                <select name="urgency" defaultValue="">
                  <option value="" disabled>
                    Select urgency
                  </option>
                  <option value="Planning ahead">Planning ahead</option>
                  <option value="Needs review soon">Needs review soon</option>
                  <option value="Equipment is down">Equipment is down</option>
                </select>
              </label>
            </div>

            <div className="contact-page-form__row">
              <label>
                Equipment type
                <input type="text" name="equipment_type" placeholder="Gearbox, hydraulic cylinder, pump, drive, etc." required />
              </label>
              <label>
                Brand or model
                <input type="text" name="brand_model" placeholder="Manufacturer, model, ratio, size, or nameplate info" />
              </label>
            </div>

            <label>
              Location
              <input type="text" name="location" autoComplete="address-level2" placeholder="City, province, country" />
            </label>

            <label>
              Repair details
              <textarea
                name="message"
                rows="7"
                placeholder="Describe the issue, symptoms, damage, leaks, noises, pressure loss, vibration, overheating, or downtime constraints."
                required
              />
            </label>

            <input type="hidden" name="to_email" value="contact@evonindustrial.com" />
            <input type="hidden" name="site_name" value="Evon Industrial Services" />

            {statusMessage && (
              <p className={`contact-page-form__status contact-page-form__status--${status}`} role="status">
                {statusMessage}
              </p>
            )}

            <Button type="submit">{isSubmitting ? 'Sending...' : 'Send Repair Request'}</Button>
          </form>
        </div>
      </section>
    </>
  );
}

export default Contact;
